import React, { useState, useEffect } from 'react';
import {
    Card,
    CardHeader,
    CardBody,
    CardTitle,
    Button,
    Row,
    Col,
    Badge,
    Alert,
    Input,
    InputGroup,
    InputGroupText
} from 'reactstrap';
import SnowFallComponent from 'components/SnowFallComponent';
import Loading from 'components/Loading';
import { empresasService } from 'services/empresasService';
import { empleadoService } from 'services/empleadoService';
import DataTable from 'components/DataTable';
import ConfirmModal from 'components/ConfirmModal';
import EmpleadoForm from './EmpleadoForm'; // IMPORT ADDED
import defaultAvatar from 'assets/img/default-avatar.png';

function Empleados() {
    const [loading, setLoading] = useState(true);
    const [empresa, setEmpresa] = useState(null);
    const [empleados, setEmpleados] = useState([]);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [modalOpen, setModalOpen] = useState(false);
    const [currentEmpleado, setCurrentEmpleado] = useState(null);

    // Estados para el Modal de Confirmación
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [empleadoToDelete, setEmpleadoToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        loadEmpresa();
    }, []);

    useEffect(() => {
        if (empresa) {
            loadEmpleados();
        }
    }, [empresa, searchTerm]);

    // Verificar si hay un ID en sessionStorage para abrir el detalle/edición automáticamente
    useEffect(() => {
        const openDetailId = sessionStorage.getItem('openDetail_empleado');
        if (openDetailId && !loading && empresa) {
            const loadEmpleadoFromAPI = async () => {
                try {
                    const response = await empleadoService.getById(openDetailId);
                    if (response.success && response.data) {
                        sessionStorage.removeItem('openDetail_empleado');
                        handleEdit(response.data);
                    } else {
                        sessionStorage.removeItem('openDetail_empleado');
                        const emp = empleados.find(c =>
                            (c.EmpleadoID || c.empleadoID)?.toString() === openDetailId
                        );
                        if (emp) handleEdit(emp);
                    }
                } catch (error) {
                    sessionStorage.removeItem('openDetail_empleado');
                    const emp = empleados.find(c =>
                        (c.EmpleadoID || c.empleadoID)?.toString() === openDetailId
                    );
                    if (emp) handleEdit(emp);
                }
            };
            loadEmpleadoFromAPI();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, empleados, empresa]);

    const loadEmpresa = async () => {
        try {
            const response = await empresasService.getAll();
            if (response.success && response.data) {
                const empresaData = Array.isArray(response.data) ? response.data : [];
                const empresaActiva = empresaData.find(emp => emp.Activo !== false && emp.activo !== false) || empresaData[0];
                setEmpresa(empresaActiva);
            }
        } catch (error) {
            setError('Error al cargar la empresa');
        }
    }

    // Función auxiliar para verificar si un usuario es administrador
    const esAdministrador = (usuario) => {
        if (!usuario) return false;
        const roles = usuario.Roles || usuario.roles || [];
        if (!Array.isArray(roles) || roles.length === 0) {
            return false;
        }

        const esAdmin = roles.some(rol => {
            const nombreRol = rol?.Nombre || rol?.nombre || '';
            return nombreRol.toLowerCase() === 'administrador';
        });

        return esAdmin;
    };

    const loadEmpleados = async () => {
        try {
            setLoading(true);
            setError('');
            if (!empresa) return;
            const buscar = searchTerm.trim() || null;
            const response = await empleadoService.getAll(empresa.EmpresaID || empresa.empresaID, buscar);
            if (response.success && response.data) {
                const empleadosData = Array.isArray(response.data) ? response.data : [];

                setEmpleados(empleadosData);
            } else {
                setError('No se encontraron empleados para esta empresa');
            }
        } catch (error) {
            setError('Error al cargar los empleados');
        } finally {
            setLoading(false);
        }
    }

    const handleAdd = () => {
        setCurrentEmpleado(null);
        setModalOpen(true);
    };

    const handleEdit = (row) => {
        setCurrentEmpleado(row);
        setModalOpen(true);
    };

    const handleDelete = (row) => {
        setEmpleadoToDelete(row);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!empleadoToDelete) return;

        const id = empleadoToDelete.EmpleadoID || empleadoToDelete.empleadoID;
        if (!id) return;

        try {
            setDeleting(true);
            const res = await empleadoService.delete(id);
            if (res.success) {
                setShowDeleteModal(false);
                setEmpleadoToDelete(null);
                loadEmpleados();
            } else {
                alert(res.message || 'Error al desactivar el empleado');
            }
        } catch (err) {
            console.error(err);
            alert(err.message || 'Error al conectarse al servicio.');
        } finally {
            setDeleting(false);
        }
    };

    const toggleModal = () => setModalOpen(!modalOpen);

    const handleSuccess = () => {
        loadEmpleados();
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-MX', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        });
    };
    const columns = [
        {
            key: 'foto',
            label: 'Foto',
            accessor: 'Foto',
            render: (value) => (
                <img
                    src={value || defaultAvatar}
                    style={{ borderRadius: '50%', objectFit: 'cover', background: '#2b2b31' }}
                    width="40"
                    height="40"
                    alt="Empleado"
                    onError={(e) => {
                        e.target.onerror = null; // Evitar loop
                        e.target.src = defaultAvatar;
                    }}
                />
            )
        },
        {
            key: 'nombre',
            label: 'Nombre',
            accessor: 'Nombre',
        },
        {
            key: 'paterno',
            label: 'Paterno',
            accessor: 'Paterno',
        },
        {
            key: 'fechaNacimiento',
            label: 'Fecha Nacimiento',
            accessor: 'FechaNacimiento',
            render: (value) => formatDate(value),
        },
        {
            key: 'rfc',
            label: 'RFC',
            accessor: 'RFC'
        },
        {
            key: 'curp',
            label: 'CURP',
            accessor: 'CURP'
        },
        {
            key: 'activo',
            label: 'Estado',
            accessor: 'Activo',
            render: (value) => (
                <Badge color={value ? 'success' : 'danger'}>
                    {value ? 'Activo' : 'Inactivo'}
                </Badge>
            ),
        },
        {
            key: 'actions',
            label: 'Acciones',
            render: (_, row) => {
                if (esAdministrador(row.Usuario || row.usuario)) {
                    return (
                        <div>
                            <Button
                                color="info"
                                size="sm"
                                className="mr-2"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(row);
                                }}
                                title="Editar"
                            >
                                <i className="tim-icons icon-pencil" />
                            </Button>
                            <span className="text-muted"></span>
                        </div>
                    );
                }
                return (
                    <div>
                        <Button
                            color="info"
                            size="sm"
                            className="mr-2"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(row);
                            }}
                            title="Editar"
                        >
                            <i className="tim-icons icon-pencil" />
                        </Button>

                        <Button
                            color="danger"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(row);
                            }}
                            title="Desactivar"
                        >
                            <i className="tim-icons icon-simple-remove" />
                        </Button>
                    </div>
                );
            },
        },
    ];

    return (
        <div className="content">
            <SnowFallComponent />
            <Row>
                <Col md="12">
                    <Card>
                        <CardHeader>
                            <Row>
                                <Col>
                                    <CardTitle tag="h4">Empleados</CardTitle>
                                </Col>
                                <Col className="text-right">
                                    <Button color="primary" onClick={handleAdd}>
                                        <i className="tim-icons icon-simple-add" /> Nuevo Empleado
                                    </Button>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md="6">
                                    <InputGroup>
                                        <InputGroupText>
                                            <i className="tim-icons icon-zoom-split" />
                                        </InputGroupText>
                                        <Input
                                            type='text'
                                            placeholder='Buscar por nombre, RFC, CURP...'
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </InputGroup>
                                </Col>
                            </Row>
                        </CardHeader>
                        <CardBody>
                            {loading ? (
                                <Loading />
                            ) : (
                                <DataTable columns={columns} data={empleados} />
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {modalOpen && (
                <EmpleadoForm
                    isOpen={modalOpen}
                    toggle={toggleModal}
                    empleado={currentEmpleado}
                    empresaId={empresa?.EmpresaID || empresa?.empresaID}
                    onSuccess={handleSuccess}
                />
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                toggle={() => {
                    setShowDeleteModal(false);
                    setEmpleadoToDelete(null);
                }}
                title="Desactivar Empleado"
                message={`¿Estás seguro de desactivar al empleado "${empleadoToDelete?.Nombre || empleadoToDelete?.nombre}"?`}
                confirmText="Desactivar"
                cancelText="Cancelar"
                onConfirm={confirmDelete}
                loading={deleting}
            />
        </div>
    );
}

export default Empleados;